/**
 * Os MODELOS de declaração emitidos pela secretaria — o par do `ModeloDeclaracao` do backend
 * (`Domain/Enums/EnumDeclaracao.cs`), com os mesmos valores.
 *
 * Os números são os do legado de propósito: aparecem em `EmissaoDeclaracao.aspx?tipo=N` e em
 * `GrantiaVaga.aspx?tipo=N`, e a rede tem atalhos, manuais e chamados de suporte que se referem a
 * eles.
 *
 * **Cada modelo é uma ROTA e uma PERMISSÃO própria**, e não um parâmetro da mesma tela: o menu do
 * legado abre a tela nova por `/proxylegado/<nome>` e a query string NÃO atravessa — um `?tipo=`
 * chegaria vazio do outro lado.
 */
export enum eModeloDeclaracao {
  /** Matriculado e frequentando — a declaração mais pedida pelo responsável. */
  Escolaridade = 1,

  /** Concluiu a série ou o ensino, com a data da conclusão. */
  Conclusao = 2,

  /** Vai sair da escola: o que concluiu, o que cursa e onde deverá ser matriculado. */
  Transferencia = 3,

  /** A conclusão de uma TURMA inteira, uma folha por estudante. */
  ConclusaoPorTurma = 4,

  /** Matriculado e cursando, com o PERCENTUAL de frequência. */
  Frequencia = 5,

  /**
   * A escolaridade de uma TURMA inteira, uma folha por estudante.
   *
   * Existia no legado e não funcionava: o rádio "Turma" aparecia, mas a emissão caía em "O modo
   * de exibição do Relatório não é suportado". Aqui o modelo existe de fato.
   */
  EscolaridadePorTurma = 6,

  /** Há vaga, com a lista de documentos e a data de validade. Tela de Garantia de Vaga. */
  AtestadoVaga = 7,

  /** NÃO há vaga. É o par do atestado e, como ele, fica registrado no log de emissão. */
  NegativaVaga = 8,

  /**
   * Atestado de frequência para o BOLSA FAMÍLIA — modelo novo, sem equivalente no legado.
   *
   * Não é uma variação de `Frequencia`: aquela declara um percentual único do período e esta traz
   * o quadro MÊS A MÊS (fevereiro a dezembro), que é o que o condicionante de educação do
   * programa exige. O percentual de cada mês é o mesmo de `EmitirFrequenciaPorPeriodo.aspx` com a
   * opção Bolsa Família marcada.
   */
  FrequenciaBolsaFamilia = 9,

  /**
   * Requerimento de transferência — o PEDIDO do responsável, não uma declaração da escola.
   *
   * Quem assina é ele; o documento termina com um recibo do histórico escolar. Fica nesta família
   * porque o esqueleto da folha é idêntico e só o corpo muda.
   */
  RequerimentoTransferencia = 10,
}

/**
 * Quem ASSINA a declaração. Muda o cargo impresso sob a linha de assinatura, e não só o nome: um
 * documento assinado por quem não tem competência para expedi-lo não vale.
 */
export enum eTipoAssinaturaDeclaracao {
  Secretario = 1,

  /** O funcionário escolhido no combo da tela, entre os lotados na escola. */
  FuncionarioResponsavel = 2,

  Diretor = 3,
}

/**
 * Os modelos da tela de COMPROVANTES (`EmitirComprovante.aspx?tipo=N` do legado).
 *
 * São documentos de MATRÍCULA, não de escolaridade — por isso são uma família própria. Três deles
 * nasceram para uma rede específica e continuam identificados como tal.
 */
export enum eModeloComprovante {
  /** Comprovante de matrícula, em DUAS vias na mesma folha (a escola fica com uma). */
  ComprovanteMatricula = 1,

  /** Declaração de matrícula — está matriculado, sem falar de frequência. */
  DeclaracaoMatricula = 2,

  /** A trajetória do estudante na rede, matrícula a matrícula. */
  TrajetoriaMatricula = 3,

  /** Certificado de conclusão do EJA. Nasceu para a rede de Contagem. */
  CertificadoConclusaoEja = 4,

  /** Requerimento de matrícula, com frente e verso e um texto por tipo de ensino. Contagem. */
  RequerimentoMatricula = 5,

  /** Ficha de matrícula. Nasceu para a rede de Magé. */
  FichaMatricula = 6,
}

/**
 * O recorte de ensino do requerimento de matrícula.
 *
 * Não é o ensino do estudante: é qual dos três TEXTOS do requerimento sai impresso — as exigências
 * de matrícula da creche, do fundamental e do EJA são diferentes.
 */
export enum eTipoEnsinoRequerimento {
  Infantil = 1,
  Fundamental = 2,
  Eja = 3,
}

/*
 * Os MARCADORES da declaração personalizada NÃO ficam mais aqui: a lista é o catálogo do
 * servidor (CatalogoMarcadoresDeclaracao), lido por declaracaoService.listarMarcadores(). Com a
 * lista escrita dos dois lados, um marcador que existisse só num deles saía impresso cru no
 * documento ou nunca podia ser inserido.
 */
